import { useState } from 'react';

/** 서버 갱신과 편집 중인 값을 분리한다. 첫 편집 시점을 기준으로 변경을 비교한다. */
export const useSettingsDraft = <TData>(
  data: TData | undefined,
  /**
   * 🔴 비교에 쓸 형태. 기본은 응답 전체다.
   *
   * 서버가 매 응답 새로 계산하는 파생값(예: PG 수수료 화면의 마지막 결제일)이 섞여 있으면,
   * 아무도 설정을 건드리지 않았는데 재조회만으로 "다른 사람이 바꿨다" 가 되어 저장이 막힌다.
   * 그런 화면은 편집 대상 컬럼만 남겨서 넘긴다.
   */
  toComparable: (value: TData) => unknown = (value) => value,
) => {
  const [edit, setEdit] = useState<{ base: TData; value: TData } | null>(null);
  const value = edit?.value ?? data;
  const baseline = edit?.base ?? data;
  const isDirty = edit !== null;

  const serialize = (target: TData | undefined): string =>
    target === undefined ? '' : JSON.stringify(toComparable(target));

  const hasRemoteChanges = isDirty && serialize(data) !== serialize(baseline);

  const update = (updater: (current: TData) => TData): void => {
    setEdit((previous) => {
      const current = previous?.value ?? data;
      const base = previous?.base ?? data;
      if (current === undefined || base === undefined) return previous;
      const next = updater(current);
      return serialize(next) === serialize(base) ? null : { base, value: next };
    });
  };

  /**
   * 🔴 재조회로 막 받아온 값이 편집 기준과 다른지.
   *
   * `hasRemoteChanges` 는 이번 렌더의 `data` 를 본다. 저장 직전에 `refetch()` 로 확인할 때는
   * 응답이 아직 state 에 반영되기 전이라 그 값을 쓸 수 없어, 받아온 값을 직접 넘겨 판단한다.
   */
  const isOutdated = (next: TData | undefined): boolean =>
    edit !== null && next !== undefined && serialize(next) !== serialize(edit.base);

  return {
    value,
    baseline,
    isDirty,
    hasRemoteChanges,
    isOutdated,
    update,
    reset: (): void => setEdit(null),
  };
};
